/* ═══════════════════════════════════════════════════════════════
   The Designer — the document's data life.

   Load a project spec, resolve the forma it prints with and the project
   values its tokens read, keep the section list the Manual rail shows,
   and save. Saving is Word's model: straight onto the spec row, on a
   ~5s debounce, flushed when you leave. No drafts, no checkout, no
   submit-for-review, no exit modal.

   R2 adds the REVIEW life: manual-level issuances (the baseline the
   marks measure against), the design-phase rolling safety snapshots,
   the punch-in revision settings, and the context the export needs.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type {
  ContentBlock,
  Issuance,
  Organization,
  ProjectManualIssuance,
  ProjectSpec,
  RevisionSettings,
  StandardConfig,
} from '../../shared/types';
import { DEFAULT_REVISION_SETTINGS, realIssuances } from '../../shared/types';
import { ProjectSpecService } from '../services/projectSpecService';
import { ProjectService } from '../services/projectService';
import { FormaService } from '../services/formaService';
import { OrgService } from '../services/orgService';
import { TeamService } from '../services/teamService';
import { ManualIssuanceService } from '../services/manualIssuanceService';
import { resolvePlaceholders } from '../../shared/forma-placeholders';
import { resolveProjectTokenContent } from '../components/ProjectFormaTokens';
import { reportError } from '../services/errorLog';
import {
  buildManifest,
  flattenForDiff,
  latestManualIssuance,
  maybeAutoSnapshot,
  pruneAutoSnapshots,
  resolveBaselineIssuance,
  sectionStatus,
  type SectionStatus,
} from './issuance';

/** ~5s, Word's cadence. Every edit also flushes on nav/close. */
export const AUTOSAVE_MS = 5000;

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export interface ManualSection {
  id: string;
  csiNumber: string;
  title: string;
  /** Issuance-aware status for the rail's chip — measured against the
   *  MANUAL-level baseline. */
  status: SectionStatus;
  issuanceName?: string;
}

/** Everything exportManualSpecs needs, kept loaded so Export never blocks. */
export interface DesignerExportContext {
  project: Record<string, unknown>;
  allSpecs: ProjectSpec[];
  formas: StandardConfig[];
  client?: Organization;
  team: Array<{ id: string; name?: string; email?: string; role?: string; title?: string }>;
}

/** What an issue produced — the record, plus the rows that now carry the
 *  frozen snapshots, so the export half of §2.10 has real data immediately. */
export interface IssueResult {
  issuance: ProjectManualIssuance;
  /** The issued sections as saved, each with its new frozen issuance. */
  specs: ProjectSpec[];
}

export interface DesignerDoc {
  loading: boolean;
  error: string | null;
  spec: ProjectSpec | null;
  /** Every spec in the project, CSI order — the Manual. */
  sections: ManualSection[];
  /** The forma this spec prints with, after the inheritance chain. */
  forma: StandardConfig | null;
  /** Resolved project/client/issuance/token/placeholder values. */
  projectMeta: Record<string, string>;
  projectName: string;
  projectNumber: string;
  /** The raw project row (Design mode: placeholder ctx, project tokens). */
  project: Record<string, unknown> | null;
  /** The project's client organization row, when one is set. */
  client: Record<string, unknown> | null;
  /** One entry per distinct issuance NAME across this project's specs —
      the axis a placeholder binding points at. Newest last. */
  projectIssuances: Array<{ name?: string; date?: string }>;
  saveState: SaveState;
  lastSavedAt: Date | null;

  // ── Review (R2) ──
  /** This project's manual issuances, oldest first. */
  manualIssuances: ProjectManualIssuance[];
  /** Design phase = no baseline anywhere; issued = a baseline exists. */
  phase: 'design' | 'issued';
  /** The open section's revision-mark baseline (manual model + legacy
   *  fallback), null when the section has none. */
  baseline: Issuance | null;
  /** The baseline's printable blocks, ready for the revisions plugin. */
  baselineBlocks: ContentBlock[] | null;
  revisionSettings: RevisionSettings;
  saveRevisionSettings: (next: RevisionSettings) => Promise<void>;
  /** Freeze per-section snapshots + record ONE manual issuance.
   *
   *  Returns the issuance it recorded AND the freshly-frozen spec rows, so
   *  the caller can export exactly what was just issued without waiting for
   *  a reload to land in state (contract §2.10: Issue = record AND export). */
  issueManual: (
    meta: { name: string; date?: string; notes?: string },
    sectionIds: string[]
  ) => Promise<IssueResult>;
  /** Everything the export helper needs, or null while loading. */
  exportCtx: DesignerExportContext | null;
  /** Re-read everything (after an issue, a restore, an external change). */
  reload: () => Promise<void>;

  /** Hand the editor's current blocks to the autosave loop. */
  onBlocksChanged: (blocks: ContentBlock[]) => void;
  /** Write now (Ctrl+S, leaving the surface). */
  flush: () => Promise<void>;
}

/** Sections sort by CSI number the way a spec book is bound. */
function csiSort(a: ManualSection, b: ManualSection): number {
  const norm = (s: string) => (s || '').replace(/\D/g, '');
  const na = norm(a.csiNumber);
  const nb = norm(b.csiNumber);
  if (na && nb && na !== nb) return na < nb ? -1 : 1;
  return (a.title || '').localeCompare(b.title || '');
}

/**
 * Resolve which forma a spec prints with. Copy-on-write chain, most specific
 * first: this spec's own copy → the forma it is explicitly bound to → the
 * project's shared copy → the firm's first master.
 *
 * A project copy supersedes the master it forked from, so a binding still
 * pointing at that master follows the copy.
 */
export function resolveFormaForSpec(
  formas: StandardConfig[],
  spec: ProjectSpec | null,
  projectId: string | undefined
): StandardConfig | null {
  if (!spec) return null;
  const specCopy = formas.find((f) => f.specId === spec.id);
  if (specCopy) return specCopy;

  const projectCopy = formas.find((f) => f.projectId === projectId && !f.specId) || null;
  let bound = formas.find((f) => f.id === spec.formaId) || null;
  if (bound && !bound.projectId && projectCopy && projectCopy.sourceFormaId === bound.id) {
    bound = projectCopy;
  }
  return bound || projectCopy || formas.find((f) => !f.projectId) || formas[0] || null;
}

export function useDesignerDoc(projectSpecId: string, userName: string): DesignerDoc {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [spec, setSpec] = React.useState<ProjectSpec | null>(null);
  const [allSpecs, setAllSpecs] = React.useState<ProjectSpec[]>([]);
  const [manualIssuances, setManualIssuances] = React.useState<ProjectManualIssuance[]>([]);
  const [formas, setFormas] = React.useState<StandardConfig[]>([]);
  const [projectMeta, setProjectMeta] = React.useState<Record<string, string>>({});
  const [projectId, setProjectId] = React.useState<string | undefined>(undefined);
  const [projectName, setProjectName] = React.useState('');
  const [projectNumber, setProjectNumber] = React.useState('');
  const [project, setProject] = React.useState<Record<string, unknown> | null>(null);
  const [client, setClient] = React.useState<Record<string, unknown> | null>(null);
  const [projectIssuances, setProjectIssuances] = React.useState<
    Array<{ name?: string; date?: string }>
  >([]);
  const [projectRow, setProjectRow] = React.useState<Record<string, unknown> | null>(null);
  const [clientOrg, setClientOrg] = React.useState<Organization | undefined>(undefined);
  const [team, setTeam] = React.useState<DesignerExportContext['team']>([]);
  const [saveState, setSaveState] = React.useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);

  // Live refs so the debounce timer and the unmount flush always see the
  // newest blocks without re-subscribing on every keystroke.
  const blocksRef = React.useRef<ContentBlock[] | null>(null);
  const specRef = React.useRef<ProjectSpec | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = React.useRef(false);
  specRef.current = spec;

  // ─── Load ───
  const load = React.useCallback(async () => {
    try {
      const ps = await ProjectSpecService.get(projectSpecId);
      if (!ps) {
        setError('This spec is no longer in the project.');
        setLoading(false);
        return;
      }
      setSpec(ps);
      specRef.current = ps;
      setProjectId(ps.projectId);

      // Everything else in parallel — a slow lookup must never blank the page.
      const [projects, specList0, formaList, orgs, assignments, members, manuals] =
        await Promise.allSettled([
          ProjectService.list(),
          ProjectSpecService.listByProject(ps.projectId),
          FormaService.list(),
          OrgService.list(),
          TeamService.listAssignments(undefined, ps.projectId),
          TeamService.list(),
          ManualIssuanceService.listByProject(ps.projectId),
        ]);

      const projectList = projects.status === 'fulfilled' ? projects.value : [];
      const project = projectList.find((p: { id: string }) => p.id === ps.projectId) as
        | Record<string, unknown>
        | undefined;
      setProjectName((project?.name as string) || '');
      setProjectNumber((project?.projectNumber as string) || '');
      setProject(project ?? null);
      setProjectRow(project || null);

      const specList = specList0.status === 'fulfilled' ? specList0.value : [ps];
      setAllSpecs(specList);

      const manualList = manuals.status === 'fulfilled' ? manuals.value : [];
      setManualIssuances(manualList);

      // Distinct REAL issuance names across the project — what a binding aims
      // at (auto snapshots are never issuances).
      const byName = new Map<string, { name?: string; date?: string }>();
      for (const s of specList) {
        for (const iss of realIssuances(s.issuances)) {
          if (iss?.name) byName.set(iss.name, { name: iss.name, date: iss.date });
        }
      }
      setProjectIssuances([...byName.values()]);

      const fl = formaList.status === 'fulfilled' ? formaList.value : [];
      setFormas(fl);

      // ── Token values: the SAME context the export pipeline resolves with ──
      const orgList = orgs.status === 'fulfilled' ? orgs.value : [];
      const client = project?.clientId ? orgList.find((o) => o.id === project.clientId) : undefined;
      setClientOrg(client);
      setClient((client as Record<string, unknown> | undefined) ?? null);
      const memberList = members.status === 'fulfilled' ? (members.value as any[]) : [];
      setTeam(
        memberList.map((tm: any) => ({
          id: tm.id,
          name: tm.name || tm.fullName || tm.displayName,
          email: tm.email,
          role: tm.role,
          title: tm.title,
        }))
      );
      // Assignments feed smart blocks, resolved lazily by the export pipeline.
      void assignments;

      const currentIssuance = realIssuances(ps.issuances).slice(-1)[0];
      const meta: Record<string, string> = {};
      meta['Project.Name'] = (project?.name as string) || '';
      meta['Project.ClientProjectName'] =
        (project?.clientProjectName as string) || (project?.name as string) || '';
      meta['Project.Number'] = (project?.projectNumber as string) || '';
      meta['Project.Address'] = (project?.address as string) || '';
      meta['Client.Name'] = (client?.name as string) || '';
      meta['Issuance.Name'] = currentIssuance?.name || '';
      meta['Issuance.Date'] = currentIssuance?.date || '';
      for (const pt of (project?.formaTokens as Array<{ id: string; content: string }>) || []) {
        meta['ProjectToken:' + pt.id] = resolveProjectTokenContent(pt.content, meta);
      }

      const effForma = resolveFormaForSpec(fl, ps, ps.projectId);
      if (effForma) {
        const phValues = resolvePlaceholders(effForma, {
          project: meta,
          issuances: realIssuances(ps.issuances),
          dateFormat: effForma.docSettings?.dateFormat,
        });
        Object.assign(meta, phValues);
      }
      Object.assign(meta, ps.tokenOverrides || {});
      setProjectMeta(meta);
      setError(null);
    } catch (err) {
      reportError('Designer:load', err);
      setError(err instanceof Error ? err.message : 'Could not open this spec.');
    }
    setLoading(false);
  }, [projectSpecId]);

  React.useEffect(() => {
    setLoading(true);
    blocksRef.current = null;
    void load();
  }, [load]);

  // Data never goes stale: re-read when anything this surface shows changes.
  React.useEffect(() => {
    const onSync = (ev: Event) => {
      const tables: string[] = (ev as CustomEvent).detail?.tables ?? [];
      if (
        tables.includes('project_specs') ||
        tables.includes('project_manual_issuances') ||
        tables.includes('projects') ||
        tables.includes('standard_config') ||
        tables.includes('organizations')
      ) {
        // A save of our own comes back through this event — don't stomp the
        // user's in-flight typing with the server's copy of it.
        if (savingRef.current || blocksRef.current) return;
        void load();
      }
    };
    window.addEventListener('specforma-sync-complete', onSync);
    return () => window.removeEventListener('specforma-sync-complete', onSync);
  }, [load]);

  // ─── Save ───
  const writeNow = React.useCallback(async () => {
    const ps = specRef.current;
    const blocks = blocksRef.current;
    if (!ps || !blocks) return;
    savingRef.current = true;
    setSaveState('saving');
    try {
      // Quiet recoverable history: before the pending edits overwrite the
      // row, roll a safety snapshot of the state they replace (rolling,
      // capped, hidden from the issuance timeline — see issuance.ts).
      const autoIssuances = maybeAutoSnapshot(ps, userName);
      const next: ProjectSpec = {
        ...ps,
        ...(autoIssuances ? { issuances: autoIssuances } : {}),
        coreContent: blocks,
        lastEdited: new Date().toISOString(),
        editedBy: userName,
      };
      const saved = await ProjectSpecService.save(next);
      specRef.current = saved;
      setSpec(saved);
      blocksRef.current = null;
      setSaveState('saved');
      setLastSavedAt(new Date());
    } catch (err) {
      reportError('Designer:save', err);
      // The user is never lied to about save state — the pending edits stay
      // in blocksRef and the next tick retries.
      setSaveState('error');
    } finally {
      savingRef.current = false;
    }
  }, [userName]);

  const onBlocksChanged = React.useCallback(
    (blocks: ContentBlock[]) => {
      blocksRef.current = blocks;
      setSaveState('dirty');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void writeNow(), AUTOSAVE_MS);
    },
    [writeNow]
  );

  const flush = React.useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await writeNow();
  }, [writeNow]);

  // Leaving mid-burst must not lose the burst.
  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const ps = specRef.current;
      const blocks = blocksRef.current;
      if (ps && blocks) {
        void ProjectSpecService.save({
          ...ps,
          coreContent: blocks,
          lastEdited: new Date().toISOString(),
          editedBy: userName,
        });
      }
    },
    [userName]
  );

  // ─── Review derivations ───

  const latestManual = React.useMemo(
    () => latestManualIssuance(manualIssuances),
    [manualIssuances]
  );

  const sections = React.useMemo(
    () =>
      allSpecs
        .map((s) => {
          const st = sectionStatus(s, latestManual);
          return {
            id: s.id,
            csiNumber: s.csiNumber || '',
            title: s.title || 'Untitled section',
            status: st.status,
            issuanceName: st.issuanceName,
          };
        })
        .sort(csiSort),
    [allSpecs, latestManual]
  );

  const baseline = React.useMemo(
    () => (spec ? resolveBaselineIssuance(spec, latestManual) : null),
    [spec, latestManual]
  );

  const baselineBlocks = React.useMemo(
    () => (baseline?.snapshot ? flattenForDiff(baseline.snapshot) : null),
    [baseline]
  );

  const phase: 'design' | 'issued' = latestManual || baseline ? 'issued' : 'design';

  const revisionSettings = spec?.revisionSettings || DEFAULT_REVISION_SETTINGS;

  const saveRevisionSettings = React.useCallback(async (next: RevisionSettings) => {
    const ps = specRef.current;
    if (!ps) return;
    // Optimistic — the paper restyles immediately; the row follows.
    const nextSpec: ProjectSpec = { ...ps, revisionSettings: next };
    specRef.current = nextSpec;
    setSpec(nextSpec);
    try {
      const saved = await ProjectSpecService.save(nextSpec);
      specRef.current = saved;
      setSpec(saved);
    } catch (err) {
      reportError('Designer:saveRevisionSettings', err);
      throw err;
    }
  }, []);

  // ─── Issue the manual ───

  const issueManual = React.useCallback(
    async (
      meta: { name: string; date?: string; notes?: string },
      sectionIds: string[]
    ): Promise<IssueResult> => {
      // Never freeze a stale row — pending edits go down first.
      await flush();
      const list = await ProjectSpecService.listByProject(
        specRef.current?.projectId || projectId || ''
      );
      const manualsNow = await ManualIssuanceService.listByProject(
        specRef.current?.projectId || projectId || ''
      );
      const prevManual = latestManualIssuance(manualsNow);
      const chosen = list.filter((s) => sectionIds.includes(s.id));
      if (!chosen.length) throw new Error('Pick at least one section to issue.');

      const entries: Array<{
        spec: ProjectSpec;
        specIssuanceId: string;
        prevBaseline: Issuance | null;
      }> = [];
      const frozenSpecs: ProjectSpec[] = [];
      for (const s of chosen) {
        const prevBaseline = prevManual ? resolveBaselineIssuance(s, prevManual) : null;
        // The issue re-baselines the section — the pre-issue safety
        // snapshots have served their purpose.
        const pruned: ProjectSpec = { ...s, issuances: pruneAutoSnapshots(s.issuances) };
        const saved = await ProjectSpecService.createIssuance(
          pruned,
          {
            coreContent: s.coreContent || [],
            systems: s.systems || [],
            ...(s.variants ? { variants: s.variants } : {}),
          },
          { name: meta.name, date: meta.date, notes: meta.notes, issuedBy: userName }
        );
        const frozen = (saved.issuances || [])[(saved.issuances || []).length - 1];
        entries.push({ spec: s, specIssuanceId: frozen.id, prevBaseline });
        frozenSpecs.push(saved);
      }

      const issuance = await ManualIssuanceService.create({
        projectId: chosen[0].projectId,
        name: meta.name,
        date: meta.date,
        notes: meta.notes,
        issuedBy: userName,
        manifest: buildManifest(entries, !prevManual),
      });
      await load();
      return { issuance, specs: frozenSpecs };
    },
    [flush, load, projectId, userName]
  );

  const forma = React.useMemo(
    () => resolveFormaForSpec(formas, spec, projectId),
    [formas, spec, projectId]
  );

  const exportCtx = React.useMemo<DesignerExportContext | null>(() => {
    if (!projectRow) return null;
    return { project: projectRow, allSpecs, formas, client: clientOrg, team };
  }, [projectRow, allSpecs, formas, clientOrg, team]);

  return {
    loading,
    error,
    spec,
    sections,
    forma,
    projectMeta,
    projectName,
    projectNumber,
    project,
    client,
    projectIssuances,
    saveState,
    lastSavedAt,
    manualIssuances,
    phase,
    baseline,
    baselineBlocks,
    revisionSettings,
    saveRevisionSettings,
    issueManual,
    exportCtx,
    reload: load,
    onBlocksChanged,
    flush,
  };
}
